import React, { useEffect, useState, useCallback } from "react";
import { MessageSquare, ChevronLeft } from "lucide-react";
import AdvisorLayout from "../components/layout/AdvisorLayout";
import CollaborationPanel from "../components/advisor/CollaborationPanel";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

// Advisor side of the conversation+tasks center. Pick a client, talk / assign
// tasks. Mirrors the client's panel inside Access Management.
export default function AdvisorMessages() {
  const [clients, setClients] = useState([]);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/pfa/clients");
      setClients(data.clients || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AdvisorLayout>
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="grid h-11 w-11 place-items-center rounded-2xl bg-emerald-600 text-white">
            <MessageSquare className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-white">
              Messages & Tasks
            </h1>
            <p className="text-sm text-slate-500">
              Talk with a client and track shared tasks & reminders.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* CLIENT LIST */}
          <div
            className={`lg:col-span-1 ${active ? "hidden lg:block" : ""}`}
          >
            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
              {clients.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-400">
                  No connected clients yet.
                </div>
              ) : (
                clients.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setActive(c)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-slate-100 dark:border-slate-800 transition ${
                      active?.id === c.id
                        ? "bg-emerald-50 dark:bg-emerald-500/10"
                        : "hover:bg-slate-50 dark:hover:bg-slate-800/60"
                    }`}
                  >
                    <span className="grid h-9 w-9 place-items-center rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-sm font-bold">
                      {c.client_name?.[0]?.toUpperCase() || "C"}
                    </span>
                    <span className="text-sm font-medium text-slate-900 dark:text-white truncate">
                      {c.client_name}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* PANEL */}
          <div className={`lg:col-span-2 ${active ? "" : "hidden lg:block"}`}>
            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
              {active ? (
                <>
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-800">
                    <button
                      onClick={() => setActive(null)}
                      className="lg:hidden grid h-8 w-8 place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="font-semibold text-slate-900 dark:text-white">
                      {active.client_name}
                    </span>
                  </div>
                  <CollaborationPanel
                    connectionId={active.id}
                    role="advisor"
                    counterpart={active.client_name}
                  />
                </>
              ) : (
                <div className="p-12 text-center text-sm text-slate-400">
                  Select a client to start the conversation.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdvisorLayout>
  );
}
